import { Trans } from '@lingui/macro'

import * as React from 'react'

import { ScreenHeadline } from '~shared/components/Headline/ScreenHeadline'
import { Grid } from '~ui/components/layout/Box'
import { H2 } from '~ui/components/typography/Heading/Heading'
import { Text } from '~ui/components/typography/Text/Text'

type TStepNumber<TStepCount extends number> = NumberRange<0, TStepCount>

export type TProcessStepProps<TStepCount extends number> = NoChildren & {
  isMobile?: boolean
  stepCount: TStepCount
  stepNumber: TStepNumber<TStepCount>
  isOptional?: boolean
  stepLabels?: {
    heading: React.ReactNode
    description: React.ReactNode
  }
  sectionLabels: {
    heading: React.ReactNode
    description: React.ReactNode
  }
  form: React.ReactElement
}

/**
 * Scaffolding of the single step (of registration or reset-password process).
 *
 * Accepts:
 *  - stepperProps `stepCount`, `stepNumber` & `isOptional` that are used to describe the progress,
 *  - `sectionLabels` with `heading` rendered as the section heading (h2) & `description` of the section,
 *  - `form` that is the content part of each step,
 *  - optional `stepLabels` with `heading` rendered as the h1 & `description` below the h1.
 */
export const ProcessStep = <TStepCount extends number>({
  form,
  stepCount,
  stepNumber,
  stepLabels,
  sectionLabels,
  isMobile = false,
  isOptional = false,
}: TProcessStepProps<TStepCount>) => {
  return (
    <Grid as={`form`} rowGap={`largePlus`}>
      <Grid rowGap={`small`} textAlign={isMobile ? `center` : `left`}>
        {/* STEPPER */}
        {stepNumber > 0 && (
          <Text fontStyle={`bold`}>
            <Trans>Step</Trans> {stepNumber}
            <Text as={`span`} inherit contrast={`low`}>
              {` / ${stepCount}`}
              {isOptional && ` - `}
              {isOptional && <Trans>optional</Trans>}
            </Text>
          </Text>
        )}

        <Grid rowGap={`largePlus`}>
          {/* MAIN HEADING */}
          {stepLabels && (
            <ScreenHeadline
              isMobile={isMobile}
              title={stepLabels.heading}
              subtitle={stepLabels.description}
              noBottomMargin
            />
          )}

          {/* SECTION HEADING */}
          <Grid rowGap={`small`} textAlign={isMobile ? `center` : `left`}>
            <H2 lineHeight={1.2} fontStyle={`bold`} fontSize={`l24`} textAlign={`inherit`}>
              {sectionLabels.heading}
            </H2>
            <Text contrast={`low`}>{sectionLabels.description}</Text>
          </Grid>
        </Grid>
      </Grid>

      {/* SECTION FORM */}
      {form}
    </Grid>
  )
}
